/**
 * 5-stage pedagogically-realistic M&A process.
 * Each stage has: title, description, action label and (optionally) a chance
 * of failure that can derail the deal.
 */

export const ACQUISITION_STAGES = [
  {
    id: 1,
    key: 'due_diligence',
    title: 'Audit de due diligence',
    icon: 'FileSearch',
    description:
      "Le DAF et son équipe analysent les comptes, contrats, flotte et personnel du concurrent. Ils identifient les actifs solides, les risques cachés (dettes, litiges) et un point faible exploitable lors de la négociation.",
    actionLabel: 'Lancer la due diligence',
    durationDays: 7,
    failureChance: 0.0, // jamais d'échec à cette étape
  },
  {
    id: 2,
    key: 'loi',
    title: "Lettre d'intention (LOI)",
    icon: 'FileSignature',
    description:
      "Vous envoyez officiellement une Lettre d'Intention non engageante au dirigeant du concurrent. Ce document encadre les négociations à venir et engage les deux parties à une exclusivité temporaire. Le dirigeant peut refuser d'ouvrir les négociations.",
    actionLabel: 'Envoyer la LOI',
    durationDays: 5,
    // Refus selon notoriété/réputation : un repreneur inconnu se fait jeter.
    // 15/06/2026 — fix incohérence : avec une réputation/notoriété au
    // maximum (score ≥ 90), AUCUN refus possible. Et quand un refus arrive
    // avec un bon score, le message ne doit plus accuser à tort la
    // réputation (cf. failureMessageFn).
    failureChanceFn: ({ reputation, notoriety }) => {
      const score = (reputation || 0) * 0.6 + (notoriety || 0) * 0.4;
      if (score >= 90) return 0;
      if (score >= 70) return 0.05;
      if (score >= 50) return 0.15;
      if (score >= 30) return 0.30;
      return 0.50;
    },
    failureLabel: 'LOI refusée par le dirigeant',
    failureMessageFn: ({ reputation, notoriety }) => {
      const score = (reputation || 0) * 0.6 + (notoriety || 0) * 0.4;
      if (score >= 50) {
        return "Le dirigeant du concurrent décline votre Lettre d'Intention : il ne souhaite pas vendre dans les conditions actuelles du marché. Votre réputation n'est pas en cause — retentez dans 90 jours, sa position pourrait évoluer.";
      }
      return "Le dirigeant du concurrent estime votre réputation insuffisante et refuse d'ouvrir les discussions. Améliorez votre notoriété et votre réputation avant de retenter dans 90 jours.";
    },
    failureMessage:
      "Le dirigeant du concurrent estime votre réputation insuffisante et refuse d'ouvrir les discussions. Améliorez votre notoriété et votre réputation avant de retenter dans 90 jours.",
  },
  {
    id: 3,
    key: 'negotiation',
    title: 'Négociation du prix',
    icon: 'HandshakeIcon',
    description:
      "Le DAF propose un prix calculé à partir du CA, de la flotte, des contrats actifs et de la dette. Vous pouvez accepter, négocier à la baisse (-15 %) ou tenter une offre agressive (-25 %). La réussite dépend de votre réputation et de la santé du concurrent.",
    actionLabel: 'Ouvrir la négociation',
    durationDays: 10,
    // L'étape elle-même ne peut pas échouer (le joueur choisit), mais
    // les choix négociation -15 / -25 ont leurs propres taux de succès.
    failureChance: 0.0,
  },
  {
    id: 4,
    key: 'antitrust',
    title: 'Validation Autorité de la Concurrence',
    icon: 'Scale',
    description:
      "L'Autorité de la Concurrence française examine la concentration pour vérifier qu'elle ne fausse pas le marché du transport routier. L'examen prend ~15 jours. Plus votre part de marché combinée est élevée, plus le risque de blocage augmente.",
    actionLabel: 'Notifier la concentration',
    durationDays: 15,
    failureChanceFn: ({ combinedMarketShare = 0 }) => {
      // Part de marché combinée (joueur + cible) en % du marché local.
      if (combinedMarketShare < 25) return 0.02;
      if (combinedMarketShare < 40) return 0.10;
      if (combinedMarketShare < 60) return 0.30;
      return 0.60;
    },
    failureLabel: 'Concentration refusée',
    failureMessage:
      "L'Autorité de la Concurrence considère que votre fusion créerait une position dominante. Le rachat est interdit. Vos frais d'instruction (~5 000 €) sont à votre charge.",
    failureCost: 5000,
  },
  {
    id: 5,
    key: 'closing',
    title: 'Signature & Closing',
    icon: 'CheckCircle',
    description:
      "Signature des actes définitifs chez le notaire. Le prix négocié est débité de votre trésorerie. Vous prenez possession de la flotte, des contrats en cours et du dépôt. Les chauffeurs ont le choix de vous suivre ou non.",
    actionLabel: 'Procéder au closing',
    durationDays: 3,
    failureChance: 0.0,
  },
];

export const TOTAL_ACQUISITION_DAYS = ACQUISITION_STAGES.reduce(
  (s, st) => s + (st.durationDays || 0),
  0,
);

export function getStageById(id) {
  return ACQUISITION_STAGES.find(s => s.id === id) || null;
}
